import React, { useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import SignaturePad from 'react-signature-pad-wrapper';
import Dropzone from 'react-dropzone';

import { useToast } from 'context/ToastContext';
import { toastConstant } from 'constants/toastmessage';
import { uuidv4 } from 'common/uuidGenerator';
import { getPresignedURLOpen } from 'services/Settings';
import { axiosInstance } from 'common/apiHandler';

const name = 'signature';

export default function SignatureModal({ isFormBuilder, onHide = () => {} }) {
  const { setValue, getValues } = useFormContext();
  const { addToast } = useToast();

  const signatureRef = useRef();
  const [isUploading, setIsUploading] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);

  const [selectedSignatureImage, setSelectedSignatureImage] = useState(null);

  const handleRemoveSignature = () => {
    setSelectedSignatureImage(null);
    if (signatureRef.current) {
      signatureRef.current.clear();
    }
  };

  const handleSave = async () => {
    if ((signatureRef.current && !signatureRef.current.isEmpty()) || selectedSignatureImage) {
      try {
        const dataURL = selectedSignatureImage || signatureRef.current.toDataURL();

        // Convert dataURL to blob and upload to S3
        const response = await fetch(dataURL);
        const blob = await response.blob();

        // Create a file from the blob
        const file = new File([blob], 'signature.png', { type: 'image/png' });

        // Upload to S3 using the same logic
        setIsUploading(true);

        const tenantId = getValues('tenant_id');
        const user_id = getValues('created_by') || 'client-signature';
        const tenantSk = `firm#${tenantId}`;
        let fileSize = file?.size || '0';
        let url = `public/file_manager/${tenantId}/${user_id}/signature_image_${uuidv4()}.png`;

        let s3Response = await getPresignedURLOpen(tenantId, tenantSk, url, fileSize);
        let formData = new FormData();

        Object.entries(s3Response?.data?.fields).forEach(([k, v]) => {
          formData.append(k, v);
        });

        formData.append('file', file);

        let responseUpload = await axiosInstance.post(s3Response?.data?.url, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });

        if (responseUpload) {
          setValue(name, url, { shouldValidate: true }); // Save S3 URL to form
          setValue('signature-preview', dataURL); // Save S3 URL to form
          addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, 'Signature saved and uploaded successfully');
          onHide(); // Close the modal
        }
      } catch (error) {
        console.error('Error saving signature:', error);
        addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.ERROR, 'Failed to save signature. Please try again.');
      } finally {
        setIsUploading(false);
      }
    } else {
      addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.ERROR, 'Please draw a signature before saving.');
    }
  };

  return (
    <Dialog
      className="common-popup-style"
      header={<></>}
      footer={
        <div className="footer-btn">
          <Button label="Cancel" icon="pi pi-times" className="p-button p-button-primary p-button-sm outline ms-2" onClick={onHide} />
          <Button
            label="Save Signature"
            className="p-button p-button-primary p-button-sm ms-2"
            onClick={handleSave}
            loading={isUploading}
            disabled={isUploading}
          />
        </div>
      }
      visible
      style={{ width: '60vw' }}
      modal
      onHide={() => null}
      closable={false}
    >
      <div className="w-100">
        <div className="modal-tabs">
          <button
            type="button"
            className={`modal-tab ${tabIndex === 0 ? 'active' : ''}`}
            data-tab="draw"
            onClick={() => {
              setTabIndex(0);
              handleRemoveSignature();
            }}
          >
            Draw
          </button>
          <button
            type="button"
            className={`modal-tab ${tabIndex === 1 ? 'active' : ''}`}
            data-tab="upload"
            onClick={() => {
              setTabIndex(1);
              handleRemoveSignature();
            }}
          >
            Upload
          </button>
        </div>

        {/* Hidden file upload input */}
        {tabIndex === 1 && (
          <>
            {!selectedSignatureImage ? (
              <Dropzone
                multiple={false}
                accept="image/*"
                onDrop={(acceptedFiles) => {
                  const file = acceptedFiles?.[0];
                  if (file.size > 5 * 1024 * 1024) {
                    addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.ERROR, 'File size should not exceed 5 MB.');
                    return;
                  }
                  setSelectedSignatureImage(URL.createObjectURL(file));
                }}
              >
                {({ getRootProps, getInputProps }) => (
                  <section style={{ pointerEvents: isFormBuilder ? 'none' : 'auto' }}>
                    <div {...getRootProps()}>
                      <input {...getInputProps()} directory="" />

                      <div className="text-center d-flex flex-column justify-content-center align-items-center file-manager-uploader mb-2">
                        <i className="pi pi-cloud-upload mb-2 pointer"></i>
                        <p className="F-size14">
                          <span style={{ color: '#3853bd', fontWeight: 600 }}>Click to Upload</span> or drag and drop
                        </p>
                      </div>
                    </div>
                  </section>
                )}
              </Dropzone>
            ) : (
              <div style={{ textAlign: 'center' }}>
                <img
                  src={selectedSignatureImage}
                  alt="Signature Preview"
                  style={{
                    maxWidth: '100%',
                    height: '200px',
                    border: '1px solid #ccc',
                    borderRadius: '8px',
                  }}
                />
              </div>
            )}
          </>
        )}

        {/* Drawing Canvas - Show when drawing mode is active */}
        {tabIndex === 0 && (
          <div className="signature-canvas-container mb-3">
            <div className="signature-canvas-wrapper">
              <SignaturePad
                ref={signatureRef}
                canvasProps={{
                  className: 'signature-canvas',
                  style: {
                    borderRadius: '8px',
                    width: '100%',
                    height: '200px',
                    cursor: 'crosshair',
                  },
                }}
                options={{
                  penColor: '#000000',
                  backgroundColor: '#ffffff',
                }}
              />
            </div>
          </div>
        )}
        <div className="mb-3">
          <Button
            label="Clear"
            icon="pi pi-refresh"
            className="p-button p-button-primary p-button-sm outline"
            onClick={handleRemoveSignature}
          />
        </div>

        {/* Consent statement below drawing component */}
        <div
          className="signature-consent mt-3"
          style={{
            borderRadius: '6px',
            padding: '15px',
            textAlign: 'center',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px',
              fontSize: '14px',
            }}
          >
            <i className="pi pi-info-circle" style={{ fontSize: '18px' }}></i>
            <span>
              By providing your signature above, you acknowledge that all the information provided in this form is true and accurate to the
              best of my knowledge.
            </span>
          </div>
        </div>
      </div>
    </Dialog>
  );
}
